import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';
import {daysInStage, stageAgeColor} from '../utils';

const STAGE_COLORS = { applied: 'bg-gray-100 text-gray-700', screening: 'bg-yellow-50 text-yellow-700', interview: 'bg-blue-50 text-blue-700', offer: 'bg-purple-50 text-purple-700', hired: 'bg-green-50 text-green-700', rejected: 'bg-red-50 text-red-700' };

export default function Applicants() {
  const [applicants, setApplicants] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stages, setStages] = useState([]);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', jobId: '', source: '' });
  const [dupWarning, setDupWarning] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [bulkTemplateId, setBulkTemplateId] = useState('');

  const load = () => {
    const params = new URLSearchParams();
    params.set('archived', showArchived);
    if (search) params.set('search', search);
    if (stageFilter) params.set('stage', stageFilter);
    if (sourceFilter) params.set('source', sourceFilter);
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    api(`/ats/applicants?${params}`).then(r => r.ok ? r.json() : []).then(setApplicants).catch(() => {});
    api('/ats/jobs').then(r => r.ok ? r.json() : []).then(setJobs).catch(() => {});
    api('/ats/pipeline-stages').then(r => r.ok ? r.json() : []).then(setStages).catch(() => {});
    api('/ats/email-templates').then(r => r.ok ? r.json() : []).then(setEmailTemplates).catch(() => {});
  };
  useEffect(() => { load(); }, [showArchived, stageFilter, sourceFilter, fromDate, toDate]);

  const checkDuplicate = async (email) => {
    if (!email) { setDupWarning(null); return; }
    const r = await api(`/ats/applicants/duplicate-check?email=${encodeURIComponent(email)}${form.jobId ? `&jobId=${form.jobId}` : ''}`);
    if (r.ok) { const d = await r.json(); setDupWarning(d.duplicate ? d : null); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await api('/ats/applicants', { method: 'POST', body: JSON.stringify({ ...form, jobId: form.jobId || null, source: form.source || null }) });
    setForm({ firstName: '', lastName: '', email: '', phone: '', jobId: '', source: '' });
    setShowForm(false); setDupWarning(null); load();
  };

  const stageNames = stages.length > 0 ? stages.map(s => s.name.toLowerCase()) : ['applied', 'screening', 'interview', 'offer', 'hired', 'rejected'];
  const SOURCES = ['LinkedIn', 'Indeed', 'Referral', 'Company Website', 'Job Fair', 'Recruiter', 'Other'];

  const filtered = applicants;

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Applicants</h2>
        <button onClick={() => setShowForm(true)} className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium">+ Add Applicant</button>
        {selected.size > 0 && (
          <button onClick={() => setShowBulkEmail(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">Email {selected.size} Selected</button>
        )}
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">New Applicant</h3>
          {dupWarning && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              ⚠️ Possible duplicate: {dupWarning.matches.map(m => `${m.firstName} ${m.lastName}`).join(', ')} already exists with this email.
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input required value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input required value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} onBlur={e => checkDuplicate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Posting</label>
                <select value={form.jobId} onChange={e => setForm({ ...form, jobId: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600">
                  <option value="">None</option>
                  {jobs.filter(j => j.status === 'open').map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600">
                  <option value="">Select...</option>
                  {['LinkedIn', 'Indeed', 'Referral', 'Company Website', 'Job Fair', 'Recruiter', 'Other'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium">Add</button>
              <button type="button" onClick={() => { setShowForm(false); setDupWarning(null); }} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <form onSubmit={e => { e.preventDefault(); load(); }} className="flex gap-2">
          <input placeholder="Search name or email..." value={search} onChange={e => setSearch(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 w-64" />
          <button type="submit" className="bg-teal-600 text-white px-3 py-2 rounded-lg hover:bg-teal-700 text-sm">Search</button>
        </form>
        <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600">
          <option value="">All Stages</option>
          {stageNames.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600">
          <option value="">All Sources</option>
          {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="From" />
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="To" />
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} className="rounded border-gray-300 text-teal-600" />
          Archived
        </label>
        {(search || stageFilter || sourceFilter || fromDate || toDate) && <button onClick={() => { setSearch(''); setStageFilter(''); setSourceFilter(''); setFromDate(''); setToDate(''); }} className="text-sm text-gray-500 hover:text-gray-700">Clear</button>}
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">In Stage</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applied</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map(a => (
              <tr key={a.id} className={`hover:bg-gray-50 ${a.archived ? 'opacity-60' : ''}`}>
                <td className="px-6 py-4"><input type="checkbox" checked={selected.has(a.id)} onChange={() => { const n = new Set(selected); n.has(a.id) ? n.delete(a.id) : n.add(a.id); setSelected(n); }} className="w-3.5 h-3.5 rounded border-gray-300 text-teal-600" /></td>
                <td className="px-6 py-4">
                  <Link to={`/ats/applicants/${a.id}`} className="text-teal-600 hover:text-teal-800 font-medium">{a.firstName} {a.lastName}</Link>
                </td>
                <td className="px-6 py-4 text-gray-600 text-sm">{a.email || '—'}</td>
                <td className="px-6 py-4 text-gray-600">{a.jobTitle || '—'}</td>
                <td className="px-6 py-4 text-gray-600 text-sm">{a.source || '—'}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${STAGE_COLORS[a.stage] || 'bg-gray-100 text-gray-600'}`}>{a.stage}</span>
                </td>
                <td className={`px-6 py-4 text-sm ${stageAgeColor(a.updatedAt)}`}>{daysInStage(a.updatedAt) || '—'}</td>
                <td className="px-6 py-4 text-gray-500 text-sm">{a.appliedAt ? new Date(a.appliedAt).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="text-center py-12 text-gray-500">No applicants found.</div>}
      </div>

      {showBulkEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowBulkEmail(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Send Bulk Email</h3>
            <p className="text-sm text-gray-500 mb-4">Send an email to {selected.size} selected applicant{selected.size !== 1 ? 's' : ''}.</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
              <select value={bulkTemplateId} onChange={e => setBulkTemplateId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Select a template...</option>
                {emailTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowBulkEmail(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button disabled={!bulkTemplateId} onClick={async () => {
                await api('/ats/applicants/bulk-email', {method: 'POST', body: JSON.stringify({applicantIds: [...selected], templateId: bulkTemplateId})});
                setShowBulkEmail(false); setBulkTemplateId(''); setSelected(new Set());
              }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Send</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
